import { useEffect } from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  FormModal,
  Input,
  ResponsiveGrid,
  Select,
} from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { COUNTRY_OPTIONS } from "../../../lib/countries";
import {
  useUpdateTeamBilling,
  type BillingInfo,
  type UpdateBillingPayload,
} from "../../../lib/queries/teams";
import type { ID } from "../../../lib/id";

const required = (msg = "Povinné pole") => z.string().trim().min(1, msg);

const schema = z.object({
  contact_name: required("Zadejte kontaktní osobu"),
  contact_email: z
    .string()
    .trim()
    .optional()
    .default("")
    .refine((v) => v === "" || /.+@.+\..+/.test(v), { message: "Neplatný e-mail" }),
  contact_phone: z.string().trim().optional().default(""),
  subject_name: z.string().trim().optional().default(""),
  street: required("Zadejte ulici"),
  city: required("Zadejte město"),
  postal_code: required("Zadejte PSČ"),
  country_code: required().length(2, "Vyberte stát"),
  ico: z.string().trim().optional().default(""),
  dic: z.string().trim().optional().default(""),
});

type FormValues = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

function defaultsFor(billing: BillingInfo | null | undefined): FormValues {
  return {
    contact_name: billing?.contact_name ?? "",
    contact_email: billing?.contact_email ?? "",
    contact_phone: billing?.contact_phone ?? "",
    subject_name: billing?.subject_name ?? "",
    street: billing?.street ?? "",
    city: billing?.city ?? "",
    postal_code: billing?.postal_code ?? "",
    country_code: billing?.country_code ?? "CZ",
    ico: billing?.ico ?? "",
    dic: billing?.dic ?? "",
  };
}

interface Props {
  teamId: ID;
  billing: BillingInfo | null | undefined;
  open: boolean;
  onClose: () => void;
}

export function EditBillingModal({ teamId, billing, open, onClose }: Props) {
  const update = useUpdateTeamBilling(teamId);
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues, unknown, FormOutput>({
    resolver: zodResolver(schema) as Resolver<FormValues, unknown, FormOutput>,
    defaultValues: defaultsFor(billing),
    mode: "onTouched",
  });

  useEffect(() => {
    if (open) reset(defaultsFor(billing));
  }, [open, billing, reset]);

  async function onSubmit(values: FormOutput) {
    const payload: UpdateBillingPayload = {
      contact_name: values.contact_name,
      contact_email: values.contact_email,
      contact_phone: values.contact_phone,
      subject_name: values.subject_name,
      street: values.street,
      city: values.city,
      postal_code: values.postal_code,
      country_code: values.country_code.toUpperCase(),
      ico: values.ico,
      dic: values.dic,
    };
    try {
      await update.mutateAsync(payload);
      onClose();
    } catch (err) {
      apiErrorToFormErrors<FormValues>(err, setError, {
        fallbackMessage: "Fakturační údaje se nepodařilo uložit.",
      });
    }
  }

  const rootMessage = rootErrorMessage(errors);

  return (
    <FormModal
      open={open}
      title="Upravit fakturační údaje"
      submitLabel="Uložit"
      busy={update.isPending}
      error={rootMessage}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      width="lg"
    >
      <ResponsiveGrid columns={2}>
        <Input
          label="Kontaktní osoba"
          required
          {...register("contact_name")}
          error={errors.contact_name?.message}
        />
        <Input
          label="Název subjektu"
          {...register("subject_name")}
          error={errors.subject_name?.message}
        />
        <Input
          label="E-mail"
          type="email"
          {...register("contact_email")}
          error={errors.contact_email?.message}
        />
        <Input
          label="Telefon"
          {...register("contact_phone")}
          error={errors.contact_phone?.message}
        />
      </ResponsiveGrid>
      <Input
        label="Ulice a číslo popisné"
        required
        {...register("street")}
        error={errors.street?.message}
      />
      <ResponsiveGrid columns={3}>
        <Input label="Město" required {...register("city")} error={errors.city?.message} />
        <Input
          label="PSČ"
          required
          {...register("postal_code")}
          error={errors.postal_code?.message}
        />
        <Controller
          control={control}
          name="country_code"
          render={({ field }) => (
            <Select
              label="Stát"
              required
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={errors.country_code?.message}
            >
              {COUNTRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        />
      </ResponsiveGrid>
      <ResponsiveGrid columns={2}>
        <Input label="IČO" {...register("ico")} error={errors.ico?.message} />
        <Input label="DIČ" {...register("dic")} error={errors.dic?.message} />
      </ResponsiveGrid>
    </FormModal>
  );
}
